import { useMutation } from "@apollo/client";
import pick from "lodash/pick";
import React, { createContext, PropsWithChildren, useCallback, useContext, useMemo, useState } from "react";

import { addSessionMutation, AddSessionType, AddSessionVariables } from "./ExerciseSession.gql";
import { ExerciseInSession } from "./types";
import { getAllExercisesDuration } from "./utils/exercises";
import { getUserId } from "./utils/localStorage";
import { convertSessionToDbSession } from "./utils/session";

interface ExerciseSessionContextType {
    exercises: ReadonlyArray<ExerciseInSession>;
    addExercise: (exercises: ExerciseInSession) => void;
    durationOfAllExercises: number;
    finishSession: (lastExercise: ExerciseInSession | undefined) => Promise<ExerciseInSession[]>;
    clearSession: () => void;
    isSaving: boolean;
}

const ExerciseSessionContext = createContext<ExerciseSessionContextType>({
    exercises: [],
    addExercise: () => Promise.resolve([]),
    durationOfAllExercises: 0,
    finishSession: () => Promise.resolve([]),
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    clearSession: () => {},
    isSaving: false,
});

export function ExerciseSessionProvider({ children }: PropsWithChildren<object>) {
    const [exercises, setExercises] = useState<ReadonlyArray<ExerciseInSession>>([]);

    const [saveSessionToDb, { loading }] = useMutation<AddSessionType, AddSessionVariables>(addSessionMutation);

    const addExercise = useCallback<ExerciseSessionContextType["addExercise"]>((exercise) => {
        setExercises((prevExercises) => addExerciseToArray(exercise, prevExercises));
    }, []);

    const durationOfAllExercises = useMemo(() => getAllExercisesDuration(exercises), [exercises]);

    const clearSession = useCallback<ExerciseSessionContextType["clearSession"]>(() => {
        setExercises([]);
    }, []);

    const finishSession = useCallback<ExerciseSessionContextType["finishSession"]>(
        async (lastExercise) => {
            const exercisesCopy = lastExercise ? addExerciseToArray(lastExercise, exercises) : [...exercises];
            const dbSession = convertSessionToDbSession({ exercises: exercisesCopy });
            await saveSessionToDb({
                variables: {
                    data: {
                        ...pick(dbSession, [
                            "durationTotal",
                            "aiExercises",
                            "durationEndurance",
                            "durationStrength",
                            "durationRelaxation",
                            "durationMobility",
                            "durationCoordination",
                            "durationAi",
                        ]),
                        exercises: dbSession.exercises.map((x) => x.id),
                        userId: getUserId(),
                    },
                },
            });
            clearSession();
            return exercisesCopy;
        },
        [exercises, saveSessionToDb, clearSession],
    );

    const value = useMemo<ExerciseSessionContextType>(
        () => ({ exercises, addExercise, durationOfAllExercises, finishSession, clearSession, isSaving: loading }),
        [exercises, addExercise, durationOfAllExercises, finishSession, clearSession, loading],
    );

    return <ExerciseSessionContext.Provider value={value}>{children}</ExerciseSessionContext.Provider>;
}

export function useExerciseSession(): ExerciseSessionContextType {
    const context = useContext(ExerciseSessionContext);

    return context;
}

function addExerciseToArray(newExercise: ExerciseInSession, array: ReadonlyArray<ExerciseInSession>): ExerciseInSession[] {
    const existingExercise = array.find((x) => x.id === newExercise.id);
    return [
        ...array.filter((x) => x.id !== newExercise.id),
        {
            ...newExercise,
            duration: newExercise.duration + (existingExercise?.duration ?? 0),
        },
    ];
}
